/**
 * brandConfig.js — 全局品牌配置（多公司动态化）
 *
 * 从 localStorage 读取当前公司品牌数据，
 * 登录后由 API 自动填充，不再硬编码。
 */

// ── 内存缓存 ──────────────────────────────────────────────────────────────────
let _currentCompany = null;

export function setCurrentCompany(company) {
    _currentCompany = company;
    if (company) {
        localStorage.setItem('revolead_company', JSON.stringify(company));
    } else {
        localStorage.removeItem('revolead_company');
    }
}

export function getCurrentCompany() {
    if (_currentCompany) return _currentCompany;
    try {
        const cached = localStorage.getItem('revolead_company');
        if (cached) { _currentCompany = JSON.parse(cached); return _currentCompany; }
    } catch { /* ignore corrupt cache */ }
    return null;
}

// ── Dynamic getters (fallback to defaults if no company loaded) ─────────────
export function getBRAND_NAME() {
    return getCurrentCompany()?.name || 'Revolead Construction';
}
export function getBRAND_FULL() {
    const name = getCurrentCompany()?.name || 'Revolead Construction';
    return `${name} Pty Ltd`;
}
export function getBRAND_ACN() {
    return getCurrentCompany()?.acn || '696 557 614';
}
export function getBRAND_TAGLINE() {
    return getCurrentCompany()?.tagline || 'Construction Management';
}
export function getLOGO_URL() {
    return getCurrentCompany()?.logo_url || '/LOGO.jpg';
}
export function getCOLOR_PRIMARY() {
    return getCurrentCompany()?.color_primary || '#1a1a2e';
}
export function getCOLOR_ACCENT() {
    return getCurrentCompany()?.color_accent || '#c9a84c';
}

// ── Static fallback exports (for backward compat with destructured imports) ───
// These return the fallback value; components should use getBRAND_NAME() instead
export const BRAND_NAME = 'Revolead Construction';
export const BRAND_FULL = 'Revolead Construction Pty Ltd';
export const BRAND_ACN = '696 557 614';
export const BRAND_TAGLINE = 'Construction Management';
export const LOGO_URL = '/LOGO.jpg';
export const LOGO_FALLBACK_URL = '/LOGO.jpg';

// ── Company helpers ──────────────────────────────────────────────────────────
export function getDefaultCompanyInfo() {
    const c = getCurrentCompany();
    return {
        name: c?.name || BRAND_NAME,
        acn: c?.acn || BRAND_ACN,
        address: c?.address || '',
        phone: c?.phone || '',
        director: c?.director || '',
        logo_url: c?.logo_url || LOGO_URL,
        logo_fallback_url: LOGO_FALLBACK_URL,
    };
}

// ── Fetch company defaults from backend (address/phone/director) ─────────────
const API_BASE = process.env.REACT_APP_BACKEND_URL;
let _companyDefaultsCache = null;

export async function fetchCompanyDefaults() {
    if (_companyDefaultsCache) return _companyDefaultsCache;
    try {
        const token = localStorage.getItem('revolead_auth_token');
        const res = await fetch(`${API_BASE}/api/settings/company-defaults`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        _companyDefaultsCache = await res.json();
        return _companyDefaultsCache;
    } catch (err) {
        console.warn('Failed to fetch company defaults:', err);
        return { company_address: '', company_phone: '', company_director: '' };
    }
}

export async function getCompanyInfoWithDefaults() {
    const info = getDefaultCompanyInfo();
    const defaults = await fetchCompanyDefaults();
    info.address = defaults.company_address || '';
    info.phone = defaults.company_phone || '';
    info.director = defaults.company_director || '';
    return info;
}

// ── Theme System ─────────────────────────────────────────────────────────────

export const THEMES = {
  dark: {
    key: 'dark',
    name: '暗黑',
    label: 'Dark',
    preview: { header: '#1a1a2e', accent: '#c9a84c', bg: '#e5e4df' },
    vars: {
      '--theme-header-bg':    '#1a1a2e',
      '--theme-accent':       '#c9a84c',
      '--theme-accent-sub':   'rgba(201,168,76,0.15)',
      '--theme-accent-mid':   'rgba(201,168,76,0.4)',
      '--theme-page-bg':      '#e5e4df',
      '--theme-card-bg':      '#f8f7f4',
      '--theme-border':       '#e5e4df',
      '--theme-text-primary': '#1a1a2e',
      '--theme-text-muted':   '#6b7280',
      '--theme-text-light':   '#9ca3af',
      '--theme-tab-active-color': '#1a1a2e',
    },
  },
  sky: {
    key: 'sky',
    name: '天空蓝',
    label: 'Sky Blue',
    preview: { header: '#49b7ec', accent: '#ff6d00', bg: '#2596be' },
    vars: {
      '--theme-header-bg':    '#49b7ec',
      '--theme-accent':       '#ff6d00',
      '--theme-accent-sub':   'rgba(255,109,0,0.15)',
      '--theme-accent-mid':   'rgba(255,109,0,0.4)',
      '--theme-page-bg':      '#2596be',
      '--theme-card-bg':      '#e8f6ff',
      '--theme-border':       '#90d4f0',
      '--theme-text-primary': '#0c2d48',
      '--theme-text-muted':   '#1a5276',
      '--theme-text-light':   '#5dade2',
      '--theme-tab-active-color': '#0c2d48',
    },
  },
  gray: {
    key: 'gray',
    name: '浅灰',
    label: 'Light Gray',
    preview: { header: '#334155', accent: '#6366f1', bg: '#f1f5f9' },
    vars: {
      '--theme-header-bg':    '#334155',
      '--theme-accent':       '#6366f1',
      '--theme-accent-sub':   'rgba(99,102,241,0.15)',
      '--theme-accent-mid':   'rgba(99,102,241,0.4)',
      '--theme-page-bg':      '#f1f5f9',
      '--theme-card-bg':      '#ffffff',
      '--theme-border':       '#e2e8f0',
      '--theme-text-primary': '#1e293b',
      '--theme-text-muted':   '#64748b',
      '--theme-text-light':   '#94a3b8',
      '--theme-tab-active-color': '#1e293b',
    },
  },
};

const THEME_STORAGE_KEY = 'revolead_ui_theme';

export function getStoredTheme() {
  return localStorage.getItem(THEME_STORAGE_KEY) || 'dark';
}

export function applyTheme(themeKey) {
  const theme = THEMES[themeKey] || THEMES.dark;
  const root = document.documentElement;
  Object.entries(theme.vars).forEach(([prop, value]) => {
    root.style.setProperty(prop, value);
  });
  localStorage.setItem(THEME_STORAGE_KEY, theme.key);
  window.dispatchEvent(new CustomEvent('theme-changed', { detail: { theme: theme.key } }));
}

// ── Refresh brand from backend (used after Company Profile edits) ────────────
let _brandCache = null;

export async function refreshCompanyBrand() {
    try {
        const token = localStorage.getItem('revolead_auth_token');
        const res = await fetch(`${API_BASE || process.env.REACT_APP_BACKEND_URL}/api/settings/company-brand`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const brand = await res.json();
        _brandCache = brand;
        _companyDefaultsCache = null; // clear so next fetchCompanyDefaults re-fetches
        setCurrentCompany(brand);
        return brand;
    } catch (err) {
        console.warn('Failed to refresh company brand:', err);
        return null;
    }
}
